import React from "react";
import {
    Box,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    Text,
    VStack,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import type { CctvCamera } from "redux/services/cctv/types";
import { useCameraLocationName } from "../../hooks/useCameraNameLocation";

interface CctvSearchInputProps {
    searchQuery: string;
    handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    showSuggestions: boolean;
    setShowSuggestions: (show: boolean) => void;
    handleSuggestionClick: (name: string) => void;
    cameras: CctvCamera[];
}

const CctvSearchInput: React.FC<CctvSearchInputProps> = ({
    searchQuery,
    handleSearchChange,
    showSuggestions,
    setShowSuggestions,
    handleSuggestionClick,
    cameras,
}) => {
    const { CameraLocationName } = useCameraLocationName();
    // Filter suggestions
    const suggestions = cameras
        .filter(cam =>
            cam.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            cam.location.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .slice(0, 50);

    return (
        <Box position="relative" zIndex={20}>
            <InputGroup>
                <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                    placeholder="Telusuri CCTV"
                    bg="white"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => searchQuery.length > 1 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    sx={{
                        '& .Mui-focused': {
                            borderColor: '#FF0000',
                        }
                    }}
                />
            </InputGroup>
            {showSuggestions && searchQuery.length > 1 && (
                <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    zIndex={100}
                    bg="white"
                    mt={1}
                    borderRadius="md"
                    boxShadow="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    maxH="200px"
                    overflowY="auto"
                    css={{
                        "&::-webkit-scrollbar": {
                            width: "6px",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "#f1f1f1",
                            borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: "#888",
                            borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                            background: "#555",
                        },
                    }}
                >
                    <VStack align="stretch" spacing={0} divider={<Box borderBottomWidth="1px" borderColor="gray.100" />}>
                        {suggestions.map((cam) => (
                            <Flex
                                key={cam.id}
                                align="center"
                                py={2}
                                px={3}
                                cursor="pointer"
                                _hover={{ bg: "gray.50" }}
                                onClick={() => handleSuggestionClick(cam.name)}
                            >
                                <Text fontSize="sm" fontWeight="medium" marginBottom={0}>{CameraLocationName(cam.name)}</Text>
                            </Flex>
                        ))}
                        {suggestions.length === 0 && (
                            <Box p={3} textAlign="center">
                                <Text fontSize="sm" color="gray.500" marginBottom={0}>Tidak ada hasil</Text>
                            </Box>
                        )}
                    </VStack>
                </Box>
            )}
        </Box>
    );
};

export default CctvSearchInput;
